import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { oportunidadeService } from '@/services/oportunidadeService'
import { Oportunidade } from '@/types/oportunidade'
import { mensagemErroDe } from '@/components/pipeline/erros'

/** Mesmo limite do backend (`SolicitarReuniaoRequest.observacao`, máx. 2000). */
export const OBSERVACAO_SOLICITACAO_MAX = 2000

interface SolicitarReuniaoDialogProps {
  open: boolean
  empresa: string
  /** Nome do consultor da reserva; nulo em reserva antiga sem nome gravado. */
  consultor?: string | null
  reservaId: string
  /** Já existe um pedido em aberto — o envio vira "de novo" (reenvia o aviso). */
  jaSolicitada?: boolean
  /** Data do pedido em aberto (ISO), só para o texto de apoio. */
  solicitadaEm?: string | null
  onClose: () => void
  onSolicitada: (atualizada: Oportunidade | null) => void | Promise<void>
  /** Quando informado e há pedido em aberto, oferece desfazer a solicitação (não notifica). */
  onCancelada?: (atualizada: Oportunidade | null) => void | Promise<void>
}

/**
 * Back-office pede ao consultor a reunião com o cliente (entrega G, 16/10/2026). A reunião
 * deixou de ser trava para iniciar a estruturação; o pedido avisa o consultor (notificação)
 * e destaca a reserva em Minhas Reservas até a reunião acontecer.
 */
export const SolicitarReuniaoDialog: FC<SolicitarReuniaoDialogProps> = ({
  open,
  empresa,
  consultor,
  reservaId,
  jaSolicitada = false,
  solicitadaEm,
  onClose,
  onSolicitada,
  onCancelada,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [observacao, setObservacao] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // Cada abertura começa limpa — o recado de um pedido não serve para o próximo.
  useEffect(() => {
    if (open) {
      setObservacao('')
      setErro(null)
    }
  }, [open])

  const fechar = () => {
    if (!enviando) onClose()
  }

  const solicitar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      const atualizada = await oportunidadeService.solicitarReuniao(reservaId, {
        observacao: observacao.trim() || null,
      })
      enqueueSnackbar(`Reunião solicitada ao consultor — ${empresa}`, { variant: 'success' })
      await onSolicitada(atualizada ?? null)
    } catch (e) {
      setErro(mensagemErroDe(e, 'Não foi possível solicitar a reunião'))
    } finally {
      setEnviando(false)
    }
  }

  const desfazer = async () => {
    if (!onCancelada) return
    setEnviando(true)
    setErro(null)
    try {
      const atualizada = await oportunidadeService.cancelarSolicitacaoReuniao(reservaId)
      enqueueSnackbar(`Solicitação de reunião cancelada — ${empresa}`, { variant: 'info' })
      await onCancelada(atualizada ?? null)
    } catch (e) {
      setErro(mensagemErroDe(e, 'Não foi possível cancelar a solicitação'))
    } finally {
      setEnviando(false)
    }
  }

  const dataPedido = solicitadaEm ? new Date(solicitadaEm).toLocaleDateString('pt-BR') : null

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Solicitar reunião ao consultor</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {consultor || 'O consultor'} será avisado e verá o destaque em Minhas Reservas.
          {' '}Reserva: <strong>{empresa}</strong>.
        </DialogContentText>
        {jaSolicitada && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {dataPedido ? `Reunião já solicitada em ${dataPedido}.` : 'Reunião já solicitada.'} Enviar
            de novo reenvia o aviso ao consultor.
          </Alert>
        )}
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}
        <TextField
          label="Observação para o consultor (opcional)"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          autoFocus
          inputProps={{ maxLength: OBSERVACAO_SOLICITACAO_MAX }}
          helperText={`${observacao.length}/${OBSERVACAO_SOLICITACAO_MAX}`}
          placeholder="Ex.: cliente pediu para conversar sobre prazo e garantias…"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {jaSolicitada && onCancelada && (
          <Button color="inherit" size="small" onClick={desfazer} disabled={enviando}>
            Cancelar solicitação
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={fechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={solicitar}
          disabled={enviando}
          startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {jaSolicitada ? 'Solicitar novamente' : 'Solicitar reunião'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
